import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Btn, Modal } from "@/components/UI";

const suggest = (r) => Math.max(0, Math.ceil((r.shortage_to_critical - (r.open_po_qty || 0)) * 100) / 100) || r.shortage_to_critical;

// Kritik Stok → toplu sipariş: her ürün için tedarikçi kullanıcı tarafından seçilir; tedarikçi (+ para birimi) başına tek PO.
export function BulkCriticalOrderModal({ rows, suppliers, onClose, onDone }) {
  const [items, setItems] = useState(() => rows.map((r) => ({ ...r, supplier_id: "", qty: suggest(r) })));
  const [expected, setExpected] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (i, k, v) => { setErr(""); setItems((xs) => xs.map((x, j) => (j === i ? { ...x, [k]: v } : x))); };
  const setAll = (v) => { setErr(""); setItems((xs) => xs.map((x) => ({ ...x, supplier_id: v }))); };
  const groups = useMemo(() => {
    const g = {};
    items.filter((x) => x.supplier_id && Number(x.qty) > 0).forEach((x) => {
      const k = `${x.supplier_id}|${x.purchase_currency || "TRY"}`;
      (g[k] = g[k] || []).push(x);
    });
    return g;
  }, [items]);
  const submit = async () => {
    if (items.some((x) => !x.supplier_id)) return setErr("Her ürün için tedarikçi seçin");
    if (items.some((x) => !(Number(x.qty) > 0))) return setErr("Miktarlar 0'dan büyük olmalı");
    setBusy(true); setErr("");
    const done = [];
    try {
      for (const [k, ls] of Object.entries(groups)) {
        const [sid, cur] = k.split("|");
        const sup = suppliers.find((s) => s.id === sid);
        const po = await api.post("/purchase-orders", {
          supplier: sup?.name || "", supplier_id: sid, currency: cur, expected_delivery: expected || null, priority: "normal",
          notes: "Kaynak: Kritik Stok (toplu sipariş)",
          lines: ls.map((x) => ({ material_id: x.material_id, qty: Number(x.qty), unit: x.unit || "adet", unit_price: x.purchase_price || 0 })),
        });
        done.push(po.po_no || po.id);
        const doneIds = new Set(ls.map((x) => x.material_id));
        setItems((xs) => xs.filter((x) => !doneIds.has(x.material_id)));
      }
      onDone(done);
    } catch (e) {
      setErr(`${done.length ? `Oluşan: ${done.join(", ")} · ` : ""}${e?.response?.data?.detail || "Satın alma oluşturulamadı"}`);
    } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Toplu Kritik Stok Siparişi (${rows.length} ürün)`} size="lg">
      <div className="space-y-3" data-testid="bulk-critical-modal">
        <div className="flex flex-wrap gap-3 items-end text-xs">
          <label className="flex flex-col gap-1">Tümüne tedarikçi
            <select className="border rounded px-2 py-1.5" onChange={(e) => setAll(e.target.value)} defaultValue="" data-testid="bulk-critical-supplier-all">
              <option value="">Seçin…</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">Beklenen Teslim
            <input type="date" className="border rounded px-2 py-1.5" value={expected} onChange={(e) => setExpected(e.target.value)} data-testid="bulk-critical-expected" />
          </label>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-slate-50"><tr>{["Ürün", "Kalan / Kritik", "Açık PO", "Sipariş Miktarı", "Tedarikçi"].map((h) => <th key={h} className="text-left px-2 py-1.5">{h}</th>)}</tr></thead>
          <tbody>
            {items.map((x, i) => (
              <tr key={x.material_id} className="border-t border-slate-100" data-testid={`bulk-critical-row-${x.material_code || x.material_id}`}>
                <td className="px-2 py-1.5">{x.material_code ? <span className="font-mono text-[10px] text-slate-500 mr-1">{x.material_code}</span> : null}{x.material_name}</td>
                <td className="px-2 py-1.5 font-mono">{x.on_hand} / {x.critical_stock}</td>
                <td className="px-2 py-1.5 font-mono">{x.open_po_qty}</td>
                <td className="px-2 py-1.5"><input type="number" min="0" step="any" value={x.qty} onChange={(e) => set(i, "qty", e.target.value)}
                  className="w-24 border rounded px-2 py-1" data-testid={`bulk-critical-qty-${x.material_code || x.material_id}`} /></td>
                <td className="px-2 py-1.5">
                  <select value={x.supplier_id} onChange={(e) => set(i, "supplier_id", e.target.value)} className="border rounded px-2 py-1 w-48"
                    data-testid={`bulk-critical-supplier-${x.material_code || x.material_id}`}>
                    <option value="">Seçin…</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-xs text-slate-600" data-testid="bulk-critical-summary">Oluşacak satın alma: <b>{Object.keys(groups).length}</b> (tedarikçi başına bir PO)</div>
        {err && <div className="text-xs text-rose-600" data-testid="bulk-critical-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <Btn variant="secondary" onClick={onClose} data-testid="bulk-critical-cancel">Vazgeç</Btn>
          <Btn onClick={submit} disabled={busy} data-testid="bulk-critical-submit">{busy ? "Oluşturuluyor…" : "Satın Almaları Oluştur"}</Btn>
        </div>
      </div>
    </Modal>
  );
}
